/**
 * Theme-derived chart/dashboard colour family (IB-291).
 *
 * `deriveChartPalette(accentHex, theme)` is the single derivation algorithm
 * behind BOTH paths described in the design note
 * (`docs/design/ib-291-theme-derived-chart-colours-design.md` §2):
 *
 *   - **Default** accent (graphite `#5b6b8c`): the algorithm is evaluated
 *     once at authoring time and the resulting sequential/diverging
 *     literals are baked into `tokens.css` (`:root` / `[data-theme="dark"]`)
 *     — the default path stays pure-CSS, zero-JS. `chartPalette.test.ts`'s
 *     parity test mechanically proves those baked literals equal this
 *     function's output, so "one algorithm, uniform for default and
 *     custom" is enforced, not just claimed. The *categorical*
 *     (`--iw-chart-1…4`) tokens are the one exception: for a **default**
 *     user they stay the hand-tuned, sponsor-ratified literals already in
 *     `tokens.css` — untouched, never re-derived — per design §2.1.
 *   - **Custom** accent (IB-258 `AccentProvider`): this function is called
 *     at runtime and its 13 outputs (4 categorical + 6 sequential + 3
 *     diverging) are injected as inline CSS custom properties on `<html>`.
 *
 * Pure and deterministic — no `Math.random`, no time, no DOM read. Same
 * `(accentHex, theme)` always produces the same output, including the
 * safety-clamp fallback path (design §5) — required so the baked defaults
 * can be proven equal to a live call, and so Playwright visual checks never
 * flicker between runs.
 *
 * Imports `relativeLuminance`/`contrastRatio` from `accentPreference.ts`
 * (do not re-implement WCAG contrast a second time — design doc §3 note).
 */
import { contrastRatio, isValidHex, relativeLuminance } from './accentPreference'

export type ChartTheme = 'light' | 'dark'

export interface DivergingRamp {
  neg: string
  mid: string
  pos: string
}

export interface ChartPalette {
  /** `--iw-chart-1…4` — categorical/qualitative series. */
  categorical: readonly [string, string, string, string]
  /** `--iw-chart-seq-1…6` — single-hue lightness ramp, `seq-1` lightest/coolest → `seq-6` darkest/most-intense. */
  seq: readonly [string, string, string, string, string, string]
  /** `--iw-chart-div-neg/-mid/-pos` — two derived poles + a neutral (never derived) midpoint. */
  div: DivergingRamp
}

// ---------------------------------------------------------------------------
// Colour-space primitives (hex <-> HSL <-> linear-RGB <-> XYZ <-> CIE Lab).
// Hand-coded — no new runtime dependency (design §2.4/§8).
// ---------------------------------------------------------------------------

interface Rgb {
  r: number
  g: number
  b: number
}

function hexToRgb(hex: string): Rgb {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  }
}

function clamp255(n: number): number {
  return Math.min(255, Math.max(0, Math.round(n)))
}

function rgbToHex({ r, g, b }: Rgb): string {
  const toHex = (channel: number) => clamp255(channel).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

interface Hsl {
  h: number
  s: number
  l: number
}

/** RGB (0-255 channels) -> HSL (h in [0,360), s/l in [0,100]). */
function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  const delta = max - min
  if (delta === 0) {
    return { h: 0, s: 0, l: l * 100 }
  }
  const s = delta / (1 - Math.abs(2 * l - 1))
  let h: number
  switch (max) {
    case rn:
      h = ((gn - bn) / delta) % 6
      break
    case gn:
      h = (bn - rn) / delta + 2
      break
    default:
      h = (rn - gn) / delta + 4
  }
  h *= 60
  if (h < 0) h += 360
  return { h, s: s * 100, l: l * 100 }
}

/** HSL -> RGB (0-255 channels). */
function hslToRgb({ h, s, l }: Hsl): Rgb {
  const sn = Math.min(100, Math.max(0, s)) / 100
  const ln = Math.min(100, Math.max(0, l)) / 100
  const hn = ((h % 360) + 360) % 360
  const c = (1 - Math.abs(2 * ln - 1)) * sn
  const x = c * (1 - Math.abs(((hn / 60) % 2) - 1))
  const m = ln - c / 2
  let rp = 0
  let gp = 0
  let bp = 0
  if (hn < 60) {
    rp = c
    gp = x
  } else if (hn < 120) {
    rp = x
    gp = c
  } else if (hn < 180) {
    gp = c
    bp = x
  } else if (hn < 240) {
    gp = x
    bp = c
  } else if (hn < 300) {
    rp = x
    bp = c
  } else {
    rp = c
    bp = x
  }
  return { r: (rp + m) * 255, g: (gp + m) * 255, b: (bp + m) * 255 }
}

function hslToHex(hsl: Hsl): string {
  return rgbToHex(hslToRgb(hsl))
}

/** sRGB channel (0-255) -> linear-light channel [0,1] (shared with `relativeLuminance`'s formula). */
function linearizeChannel(channel: number): number {
  const c = channel / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** Linear-light [0,1] -> sRGB channel (0-255) — inverse of `linearizeChannel`. */
function delinearizeChannel(linear: number): number {
  const c = Math.min(1, Math.max(0, linear))
  const srgb = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055
  return srgb * 255
}

interface Xyz {
  x: number
  y: number
  z: number
}

/** sRGB (0-255) -> CIE XYZ (D65), via linear-light RGB. */
function rgbToXyz(rgb: Rgb): Xyz {
  const r = linearizeChannel(rgb.r)
  const g = linearizeChannel(rgb.g)
  const b = linearizeChannel(rgb.b)
  return {
    x: 0.4124564 * r + 0.3575761 * g + 0.1804375 * b,
    y: 0.2126729 * r + 0.7151522 * g + 0.072175 * b,
    z: 0.0193339 * r + 0.119192 * g + 0.9503041 * b,
  }
}

// D65 reference white, normalized to Y=1.
const D65 = { x: 0.95047, y: 1.0, z: 1.08883 }

function labF(t: number): number {
  const delta = 6 / 29
  return t > delta ** 3 ? Math.cbrt(t) : t / (3 * delta ** 2) + 4 / 29
}

interface Lab {
  l: number
  a: number
  b: number
}

function xyzToLab(xyz: Xyz): Lab {
  const fx = labF(xyz.x / D65.x)
  const fy = labF(xyz.y / D65.y)
  const fz = labF(xyz.z / D65.z)
  return {
    l: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  }
}

/** CIE76 Euclidean distance in Lab space — the "clearly distinct swatch" metric (design §2.4). */
function deltaE76(a: Lab, b: Lab): number {
  return Math.sqrt((a.l - b.l) ** 2 + (a.a - b.a) ** 2 + (a.b - b.b) ** 2)
}

// ---------------------------------------------------------------------------
// Colour-blindness (deuteranopia / protanopia) simulation.
//
// Fixed 3x3 linear-RGB transforms (Machado/Brettel-family dichromat
// approximation, full severity) — a deterministic, hand-coded constant, not
// a runtime dependency (design §2.4/§8). Applied in LINEAR light, then
// converted back to sRGB before Lab conversion for the ΔE check.
// ---------------------------------------------------------------------------

type Matrix3 = readonly [readonly [number, number, number], readonly [number, number, number], readonly [number, number, number]]

const DEUTERANOPIA_MATRIX: Matrix3 = [
  [0.625, 0.375, 0],
  [0.7, 0.3, 0],
  [0, 0.3, 0.7],
]

const PROTANOPIA_MATRIX: Matrix3 = [
  [0.567, 0.433, 0],
  [0.558, 0.442, 0],
  [0, 0.242, 0.758],
]

function applyMatrix(rgb: Rgb, matrix: Matrix3): Rgb {
  const r = linearizeChannel(rgb.r)
  const g = linearizeChannel(rgb.g)
  const b = linearizeChannel(rgb.b)
  const [row0, row1, row2] = matrix
  return {
    r: delinearizeChannel(row0[0] * r + row0[1] * g + row0[2] * b),
    g: delinearizeChannel(row1[0] * r + row1[1] * g + row1[2] * b),
    b: delinearizeChannel(row2[0] * r + row2[1] * g + row2[2] * b),
  }
}

function simulateCvd(hex: string, matrix: Matrix3): Lab {
  return xyzToLab(rgbToXyz(applyMatrix(hexToRgb(hex), matrix)))
}

/** The two CVD kinds the clamp validates against (design §2.4). */
const CVD_MATRICES: ReadonlyArray<{ name: string; matrix: Matrix3 }> = [
  { name: 'deuteranopia', matrix: DEUTERANOPIA_MATRIX },
  { name: 'protanopia', matrix: PROTANOPIA_MATRIX },
]

/** Ratified perceptual-distance floor (design §2.4, D-IB291-2) — a "clearly distinct swatch". */
export const CVD_DELTA_E_FLOOR = 15

/**
 * `true` when every pairwise combination of `hexes` stays >= {@link CVD_DELTA_E_FLOOR}
 * apart under BOTH deuteranopia and protanopia simulation. Exported for the
 * adversarial fuzz suite (R-24, Test Engineer-owned).
 */
export function passesCvdDistinctness(hexes: readonly string[]): boolean {
  return minCvdDeltaE(hexes) >= CVD_DELTA_E_FLOOR
}

/**
 * The smallest pairwise ΔE(CIE76) across all combinations of `hexes`, under
 * BOTH deuteranopia and protanopia simulation — the exact quantity
 * {@link passesCvdDistinctness} floors at {@link CVD_DELTA_E_FLOOR}.
 * Exported so the adversarial fuzz suite (R-24) can assert a magnitude, not
 * just a boolean (proving the fallback path measurably improves distance,
 * not just flips a threshold coincidentally).
 */
export function minCvdDeltaE(hexes: readonly string[]): number {
  let min = Infinity
  for (const { matrix } of CVD_MATRICES) {
    const simulated = hexes.map((hex) => simulateCvd(hex, matrix))
    for (let i = 0; i < simulated.length; i += 1) {
      for (let j = i + 1; j < simulated.length; j += 1) {
        min = Math.min(min, deltaE76(simulated[i]!, simulated[j]!))
      }
    }
  }
  return min
}

// ---------------------------------------------------------------------------
// WCAG non-text-contrast floor (SC 1.4.11) vs the theme's canvas surfaces.
// ---------------------------------------------------------------------------

/** Ratified non-text-contrast floor (design §2.4). */
export const WCAG_NON_TEXT_CONTRAST_FLOOR = 3

/**
 * The theme's canvas surfaces a chart mark must stay legible against — all
 * THREE ratified canvas literals from `tokens.css`, not just two:
 * `--iw-bg` / `--iw-surface` / `--iw-surface-raised`. Light: `#ffffff` (bg
 * AND surface — both white) / `#f7f8fb` (surface-raised). Dark: `#12161f`
 * (bg) / `#191e2a` (surface) / `#212837` (surface-raised — the HIGHEST-
 * luminance dark canvas, and so the hardest to clear; omitting it under-
 * tests the dark-theme floor).
 */
const SURFACE_LITERALS: Record<ChartTheme, readonly string[]> = {
  light: ['#ffffff', '#f7f8fb'],
  dark: ['#12161f', '#191e2a', '#212837'],
}

/** `true` when `hex` clears {@link WCAG_NON_TEXT_CONTRAST_FLOOR} against every canvas surface for `theme`. */
export function passesContrastFloor(hex: string, theme: ChartTheme): boolean {
  const luminance = relativeLuminance(hex)
  return SURFACE_LITERALS[theme].every((surface) => contrastRatio(luminance, relativeLuminance(surface)) >= WCAG_NON_TEXT_CONTRAST_FLOOR)
}

// ---------------------------------------------------------------------------
// Derivation constants (Tech Lead-ratified numeric tuning, design §2.1-§2.3 —
// tunable here without touching any consumer).
// ---------------------------------------------------------------------------

/** Hue offsets (degrees) from the accent/base hue for categorical slots 2-4 — spread, not equal 90° steps (design §2.1). */
const CATEGORICAL_HUE_OFFSETS: readonly number[] = [0, 150, 75, 210]

const CATEGORICAL_BAND: Record<ChartTheme, Hsl> = {
  light: { h: 0, s: 58, l: 42 },
  dark: { h: 0, s: 60, l: 67 },
}

/**
 * Per-slot lightness OFFSET from {@link CATEGORICAL_BAND}'s baseline —
 * staggering L a little across the 4 slots from the start, not just varying
 * hue, because dichromatic (CVD) simulation collapses much of the hue wheel
 * onto fewer distinguishable axes (a known limit of hue-only qualitative
 * palettes). A small fixed L stagger gives the CVD clamp a head start
 * without pushing any slot toward the washed-out extremes near 0/100% —
 * where saturation collapses and colours become indistinguishable to
 * EVERY viewer, defeating the purpose. Applied before any clamp.
 */
const CATEGORICAL_LIGHTNESS_STAGGER: readonly [number, number, number, number] = [0, -6, 7, -3]

/** Sequential-ramp lightness stops, `seq-1`(lightest/coolest) -> `seq-6` — monotonic per theme (design §2.2). */
const SEQUENTIAL_LIGHTNESS_STOPS: Record<ChartTheme, readonly [number, number, number, number, number, number]> = {
  light: [92, 80, 66, 52, 40, 28],
  dark: [26, 36, 48, 60, 72, 84],
}

const SEQUENTIAL_SATURATION: Record<ChartTheme, number> = {
  light: 52,
  dark: 55,
}

/** Diverging poles' hue offset from the base hue (design §2.3) — `pos` = base hue, `neg` = base + offset. */
const DIVERGING_HUE_OFFSET = 165

const DIVERGING_BAND: Record<ChartTheme, { s: number; l: number }> = {
  light: { s: 58, l: 44 },
  dark: { s: 60, l: 66 },
}

/** `div-mid` is a neutral TOKEN REFERENCE, never a derived hue — "true no-signal" (design §2.3). */
const DIVERGING_MID_VAR: Record<ChartTheme, string> = {
  light: 'var(--iw-neutral-200)',
  dark: 'var(--iw-neutral-300)',
}

/** Bounded lightness clamp walk — deterministic, fixed step (design §2.4/§5). */
const CONTRAST_CLAMP_STEP = 4
const CONTRAST_CLAMP_MAX_STEPS = 20
const CONTRAST_CLAMP_MIN_L = 4
const CONTRAST_CLAMP_MAX_L = 96

/** Bounded hue-nudge clamp walk for CVD collisions — deterministic, small fixed step per iteration. */
const CVD_CLAMP_HUE_STEP = 3
/**
 * Bounded lightness-nudge step for CVD collisions, per iteration — hue
 * alone cannot always separate 4 colours under a dichromatic simulation
 * (deuteranopia/protanopia collapse much of the hue wheel onto fewer
 * distinguishable axes, a known limit of hue-only qualitative palettes), so
 * lightness is a second degree of freedom. Capped at {@link CVD_CLAMP_L_MAX_DELTA}
 * from each slot's ORIGINAL (post-{@link CATEGORICAL_LIGHTNESS_STAGGER})
 * lightness — walking all the way to the 0%/100% extremes would desaturate
 * every hue toward the same washed-out white/black, defeating the purpose
 * (the failure mode this cap specifically prevents).
 */
const CVD_CLAMP_L_STEP = 2
const CVD_CLAMP_L_MAX_DELTA = 30
const CVD_CLAMP_MAX_ITERATIONS = 150

/**
 * Walks `hsl`'s lightness in fixed {@link CONTRAST_CLAMP_STEP} steps, toward
 * whichever direction increases contrast against the theme's canvas, until
 * {@link passesContrastFloor} passes or the bounded step budget is spent
 * (never an unbounded/non-deterministic search — design §2.4/§5).
 */
function clampForContrast(hsl: Hsl, theme: ChartTheme): Hsl {
  let candidate = hsl
  let hex = hslToHex(candidate)
  if (passesContrastFloor(hex, theme)) return candidate
  // Dark theme's canvas is near-black — legibility improves by going LIGHTER;
  // light theme's canvas is near-white — legibility improves by going DARKER.
  const direction = theme === 'dark' ? 1 : -1
  for (let step = 0; step < CONTRAST_CLAMP_MAX_STEPS; step += 1) {
    const nextL = Math.min(CONTRAST_CLAMP_MAX_L, Math.max(CONTRAST_CLAMP_MIN_L, candidate.l + direction * CONTRAST_CLAMP_STEP))
    candidate = { ...candidate, l: nextL }
    hex = hslToHex(candidate)
    if (passesContrastFloor(hex, theme)) return candidate
    if (nextL === CONTRAST_CLAMP_MIN_L || nextL === CONTRAST_CLAMP_MAX_L) break
  }
  return candidate
}

/**
 * Combined, interleaved clamp for the 4 categorical slots: each iteration
 * (a) re-runs {@link clampForContrast} on every slot at its CURRENT hue (a
 * hue nudge can shift a slot's own luminance, so contrast must be
 * re-validated after every hue change, not assumed stable from an earlier
 * pass), then (b) if the set still fails {@link passesCvdDistinctness},
 * nudges slots 2-4 apart from slot 1 by one small, fixed hue/lightness step
 * ({@link CVD_CLAMP_HUE_STEP}/{@link CVD_CLAMP_L_STEP}). Slot 1 (index 0) stays anchored on the accent's
 * own hue (design §2.1) and is never hue-nudged, only contrast-clamped.
 * Small steps + full re-validation each pass keep the walk convergent and
 * deterministic without the large, order-dependent hue jumps a coarse
 * single-pass nudge would risk (e.g. wrapping a slot into an entirely
 * different, still-failing hue band). Bounded at
 * {@link CVD_CLAMP_MAX_ITERATIONS} — never an unbounded search (design §2.4/§5).
 */
function clampCategorical(hsls: readonly [Hsl, Hsl, Hsl, Hsl], theme: ChartTheme): readonly [Hsl, Hsl, Hsl, Hsl] {
  const original = hsls
  const boundedL = (l: number) => Math.min(CONTRAST_CLAMP_MAX_L, Math.max(CONTRAST_CLAMP_MIN_L, l))

  let candidates = hsls
  for (let iteration = 0; iteration < CVD_CLAMP_MAX_ITERATIONS; iteration += 1) {
    candidates = candidates.map((hsl) => clampForContrast(hsl, theme)) as [Hsl, Hsl, Hsl, Hsl]
    const hexes = candidates.map(hslToHex)
    if (passesCvdDistinctness(hexes)) return candidates

    // Nudge magnitude scales with the iteration count and is measured from
    // each slot's ORIGINAL (pre-clamp) hue/lightness — bounded, deterministic,
    // and never compounding indefinitely toward a washed-out extreme.
    const n = iteration + 1
    const lDelta = Math.min(CVD_CLAMP_L_MAX_DELTA, n * CVD_CLAMP_L_STEP)
    candidates = [
      original[0],
      { ...original[1], h: original[1].h + CVD_CLAMP_HUE_STEP * n, l: boundedL(original[1].l + lDelta) },
      { ...original[2], h: original[2].h - CVD_CLAMP_HUE_STEP * n, l: boundedL(original[2].l - lDelta) },
      { ...original[3], h: original[3].h + CVD_CLAMP_HUE_STEP * 2 * n, l: boundedL(original[3].l + lDelta * 0.5) },
    ]
  }
  return candidates.map((hsl) => clampForContrast(hsl, theme)) as [Hsl, Hsl, Hsl, Hsl]
}

// ---------------------------------------------------------------------------
// Public derivation
// ---------------------------------------------------------------------------

function baseHueOf(accentHex: string): number {
  return rgbToHsl(hexToRgb(accentHex)).h
}

function buildCategorical(baseHue: number, theme: ChartTheme): readonly [string, string, string, string] {
  const band = CATEGORICAL_BAND[theme]
  const hsls = CATEGORICAL_HUE_OFFSETS.map((offset, index) => ({
    ...band,
    h: baseHue + offset,
    l: band.l + (CATEGORICAL_LIGHTNESS_STAGGER[index] ?? 0),
  })) as [Hsl, Hsl, Hsl, Hsl]
  const clamped = clampCategorical(hsls, theme)
  return clamped.map(hslToHex) as unknown as readonly [string, string, string, string]
}

/**
 * Sequential ramp — deliberately NOT run through {@link clampForContrast}
 * per step. A sequential ramp's whole purpose is to grade FROM the canvas
 * (representing "near zero" — design §2.2's zero-fill semantic: a real
 * lowest-value cell maps to `seq-1`, deliberately pale, distinguished from
 * "no data" which never uses a ramp step at all) TO the most intense value
 * (`seq-6`, always the high-contrast end regardless of theme direction —
 * design §2.2). Forcing every internal step to individually clear the
 * canvas-contrast floor would flatten that gradation and defeat the ramp's
 * purpose (a heatmap's low bucket is conventionally pale-on-canvas, with a
 * cell border/legend carrying the boundary — the SC 1.4.11 "conveyed another
 * way" allowance). The ratified {@link SEQUENTIAL_LIGHTNESS_STOPS} are
 * chosen so the high-contrast end (`seq-6`) clears the floor for the FULL
 * hue range on its own (verified in `chartPalette.test.ts`); only that end
 * is asserted against WCAG. Monotonicity (AC3) holds by construction: fixed
 * hue + fixed saturation + a monotonic lightness-stop array is monotonic in
 * simulated luminance.
 */
function buildSequential(baseHue: number, theme: ChartTheme): readonly [string, string, string, string, string, string] {
  const s = SEQUENTIAL_SATURATION[theme]
  const stops = SEQUENTIAL_LIGHTNESS_STOPS[theme]
  return stops.map((l) => hslToHex({ h: baseHue, s, l })) as unknown as readonly [
    string,
    string,
    string,
    string,
    string,
    string,
  ]
}

/**
 * Diverging poles — WCAG-clamped (each individually run through
 * {@link clampForContrast}), but **deliberately NOT run through
 * {@link passesCvdDistinctness}** — that clamp is scoped to the 4
 * categorical values only (design §2.4). `--iw-chart-div-neg`/`-pos` are
 * therefore NOT guaranteed colour-blind-distinct from each other for every
 * accent (a green-family custom accent can land both poles within ΔE<15
 * under deuteranopia/protanopia simulation). This is a disclosed, tracked
 * gap — **RAID R-25** (`docs/raid-log.md`) — not an oversight: `div-*` is
 * unconsumed by any shipped component today (token contract only, R-22), so
 * closing it is deferred to a Tech Lead design pass (likely a two-hue-family
 * diverging scheme) BEFORE any diverging/variance chart consumer ships. Do
 * not silently add a CVD clamp here without that design pass — a
 * single-accent-hue-rotation diverging pair (the current shape) cannot
 * generally satisfy a ΔE≥15 floor the same way the 4-slot categorical
 * hue-spread can.
 */
function buildDiverging(baseHue: number, theme: ChartTheme): DivergingRamp {
  const band = DIVERGING_BAND[theme]
  const pos = clampForContrast({ h: baseHue, ...band }, theme)
  const neg = clampForContrast({ h: baseHue + DIVERGING_HUE_OFFSET, ...band }, theme)
  return {
    pos: hslToHex(pos),
    neg: hslToHex(neg),
    mid: DIVERGING_MID_VAR[theme],
  }
}

/**
 * Derives the full 13-token chart family from an accent hex + theme. Pure,
 * deterministic (see module header). `accentHex` must already be
 * `isValidHex`-guarded by the caller (mirrors `accentContrast`'s contract);
 * an invalid hex falls back to hue `0` rather than throwing, so a defensive
 * caller never crashes the render tree over a malformed preference.
 */
export function deriveChartPalette(accentHex: string, theme: ChartTheme): ChartPalette {
  const baseHue = isValidHex(accentHex) ? baseHueOf(accentHex) : 0
  return {
    categorical: buildCategorical(baseHue, theme),
    seq: buildSequential(baseHue, theme),
    div: buildDiverging(baseHue, theme),
  }
}

/** Flattens a {@link ChartPalette} into the 13 CSS custom-property names `AccentProvider` injects/removes. */
export const CHART_PALETTE_VAR_NAMES = [
  '--iw-chart-1',
  '--iw-chart-2',
  '--iw-chart-3',
  '--iw-chart-4',
  '--iw-chart-seq-1',
  '--iw-chart-seq-2',
  '--iw-chart-seq-3',
  '--iw-chart-seq-4',
  '--iw-chart-seq-5',
  '--iw-chart-seq-6',
  '--iw-chart-div-neg',
  '--iw-chart-div-mid',
  '--iw-chart-div-pos',
] as const

/** `{ varName: value }` pairs for the 13 inline chart custom properties — what `AccentProvider` sets/removes. */
export function chartPaletteToCssVars(palette: ChartPalette): Readonly<Record<(typeof CHART_PALETTE_VAR_NAMES)[number], string>> {
  const [c1, c2, c3, c4] = palette.categorical
  const [s1, s2, s3, s4, s5, s6] = palette.seq
  return {
    '--iw-chart-1': c1,
    '--iw-chart-2': c2,
    '--iw-chart-3': c3,
    '--iw-chart-4': c4,
    '--iw-chart-seq-1': s1,
    '--iw-chart-seq-2': s2,
    '--iw-chart-seq-3': s3,
    '--iw-chart-seq-4': s4,
    '--iw-chart-seq-5': s5,
    '--iw-chart-seq-6': s6,
    '--iw-chart-div-neg': palette.div.neg,
    '--iw-chart-div-mid': palette.div.mid,
    '--iw-chart-div-pos': palette.div.pos,
  }
}
